import type { SecondFactorEnrollmentFailedAction } from '@Application/Security/Authentications/Actions/secondFactorEnrollmentFailed';
import type { SecondFactorEnrollmentStartedPayload } from '@Application/Security/Authentications/Actions/secondFactorEnrollmentStarted';
import ConfirmSecondFactorEnrollment from '@Application/Security/Authentications/Commands/ConfirmSecondFactorEnrollment';
import confirmSecondFactorEnrollment from '@Application/Security/Authentications/confirmSecondFactorEnrollment';
import startSecondFactorEnrollment from '@Application/Security/Authentications/startSecondFactorEnrollment';
import AuthenticationNotifications from '@UI/Pages/Authentication/Shared/AuthenticationNotifications';
import PairingStep from '@UI/Pages/Authentication/SecondFactorEnrollment/PairingStep';
import RecoveryCodesStep from '@UI/Pages/Authentication/SecondFactorEnrollment/RecoveryCodesStep';
import VerificationStep from '@UI/Pages/Authentication/SecondFactorEnrollment/VerificationStep';
import EnrollmentFormData from '@UI/Pages/Authentication/SecondFactorEnrollment/FormData/EnrollmentFormData';
import * as React from 'react';
import resolvePostLoginDestination from '@UI/Routing/resolvePostLoginDestination';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateValidationFromExternalSource } from '@verclam/form-validator';
import { useDispatch } from '@verclam/synapse';
import { Card } from '@verclam/ui-kit';

type EnrollmentStep =
	| { readonly type: 'pairing' }
	| { readonly type: 'verification' }
	| { readonly type: 'recovery-codes'; readonly recoveryCodes: readonly string[] };

/**
 * A mandatory step imposed on the first connection of any password account: no exit exists on this
 * screen besides the `Continuer` button of its last step. The route guard (`RequireChallenge`) is
 * what makes the constraint hold: reloading, or navigating away by URL, only ever lands back on
 * `/login` or `/account/second-factor/enrollment` itself.
 */
const SecondFactorEnrollmentPage: React.FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [step, setStep] = React.useState<EnrollmentStep>({ type: 'pairing' });
	const [pairing, setPairing] = React.useState<SecondFactorEnrollmentStartedPayload | null>(null);
	const [pairingError, setPairingError] = React.useState<string | undefined>(undefined);
	const [isPairingLoading, setIsPairingLoading] = React.useState(false);
	const [formData, setFormData] = React.useState(() => EnrollmentFormData.createEmpty());
	const [isVerifying, setIsVerifying] = React.useState(false);

	const handleStartEnrollment = React.useCallback(() => {
		setIsPairingLoading(true);
		setPairingError(undefined);

		dispatch(startSecondFactorEnrollment())
			.then((action) => {
				if (action.type !== 'SECOND_FACTOR_ENROLLMENT_STARTED') {
					return;
				}

				setPairing(action.payload);
			})
			.catch((action) => {
				setPairingError(
					action.payload?.message ?? 'Le démarrage de l’activation a échoué. Réessayez.',
				);
			})
			.finally(() => setIsPairingLoading(false));
	}, [dispatch]);

	React.useEffect(() => {
		// `step` already starts at `pairing` from its initial state, so a fresh mount only needs to
		// kick off the pairing request itself. `handleStartEnrollment` only depends on `dispatch`,
		// which is stable, so this effect runs once per mount.
		handleStartEnrollment();
	}, [handleStartEnrollment]);

	const handleContinueToVerification = React.useCallback(() => {
		setStep({ type: 'verification' });
	}, []);

	const handleCodeChange = React.useCallback((value: string) => {
		setFormData((current) => current.setCode(value));
	}, []);

	const handleVerifySubmit = React.useCallback(
		(event: React.SyntheticEvent<HTMLFormElement>) => {
			event.preventDefault();

			if (!formData.isFormDataValid()) {
				setFormData(formData.showErrors());
				return;
			}

			setIsVerifying(true);

			dispatch(confirmSecondFactorEnrollment(new ConfirmSecondFactorEnrollment(formData.code)))
				.then((action) => {
					if (action.type !== 'SECOND_FACTOR_ENROLLED') {
						return;
					}

					setStep({ type: 'recovery-codes', recoveryCodes: action.payload.recoveryCodes });
				})
				.catch((action: SecondFactorEnrollmentFailedAction) => {
					const serverFieldErrors = action.payload?.fieldErrors ?? [];

					// The server names no field — the step has only one — so the field is marked with a
					// short wording, and the server's own explanation stays in the toast.
					const fieldErrors =
						serverFieldErrors.length > 0
							? serverFieldErrors
							: [{ field: 'code', message: 'Code incorrect.' }];

					updateValidationFromExternalSource({
						action: { type: action.type, payload: { fieldErrors } },
						eventNames: 'SECOND_FACTOR_ENROLLMENT_FAILED',
						formDataUpdater: setFormData,
					});
				})
				.finally(() => setIsVerifying(false));
		},
		[dispatch, formData],
	);

	const handleFinish = React.useCallback(() => {
		navigate(resolvePostLoginDestination(location.state), { replace: true });
	}, [navigate, location]);

	const renderStep = (): React.ReactElement => {
		switch (step.type) {
			case 'pairing': {
				return (
					<PairingStep
						pairing={pairing}
						error={pairingError}
						isLoading={isPairingLoading}
						onRetry={handleStartEnrollment}
						onContinue={handleContinueToVerification}
					/>
				);
			}
			case 'verification': {
				return (
					<VerificationStep
						formData={formData}
						onCodeChange={handleCodeChange}
						onSubmit={handleVerifySubmit}
						isSubmitting={isVerifying}
					/>
				);
			}
			case 'recovery-codes': {
				return <RecoveryCodesStep recoveryCodes={step.recoveryCodes} onFinish={handleFinish} />;
			}
		}
	};

	return (
		<React.Fragment>
			<Card title="Activation du second facteur" width="100%" maxWidth="30rem">
				{renderStep()}
			</Card>
			<AuthenticationNotifications />
		</React.Fragment>
	);
};

export default SecondFactorEnrollmentPage;
